/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import React from "react";
import { Panel, Text, BackgroundPanel, SwitchButtons, TableAdvanced } from "vr-react-components";
import { getFormattedTime } from "../../../utils/TimeUtils";
import { useSelector, useDispatch } from "react-redux";
import JobTooltip from "../../../widgets/JobTooltip";
import deepEqual from "deep-equal";
import BarPanelAux from "./BarPanelAux";
import { setLeftPanelSelector } from "../../../store/slice/roomSlices/jobRoomSlice";

const LeftPanelDesktop = React.memo(({ width, height }) => {
    const lastTenRunsTable = useSelector((state) => state.jobroom.lastTenRunsTable, deepEqual);
    const selectedChart = useSelector((state) => state.jobroom.leftPanelSelector);
    const selectedJob = useSelector((state) => state.jobroom.selectedJob);
    const lastDefinitionChangesData = useSelector((state) => state.jobroom.lastDefinitonChangesData);
    const canModify = useSelector((state) => state.private.device.canModify);

    const dispatch = useDispatch();

    const innerWidth = width * 0.9;
    const innerHeight = height * 0.9;
    const padding = innerWidth / 50;
    const textSize = 45 / innerWidth;

    return (
        <BackgroundPanel backgroundColor={"#121212"} width={width} height={height}>
            <Panel
                width={innerWidth - padding}
                height={innerHeight * 0.5 - padding}
                backgroundColor={"#262626"}
                panel
                aframeClasses={canModify ? "collidable" : ""}
                position={`0 ${-innerHeight / 2 + (innerHeight * 0.5 - padding) / 2
                    } 0.001`}
                title="Last 10 runs data"
                textSizeDecimal={textSize * 0.8}
            >
                <a-entity position={`0 ${innerHeight * 0.2 - innerHeight * 0.05} 0`}>
                    <SwitchButtons
                        height={innerHeight * 0.05}
                        width={innerWidth - padding}
                        aframeClasses={canModify ? "collidable" : ""}
                        options={[
                            {
                                element: "Duration Chart",
                                onClickHandler: () => {
                                    dispatch(setLeftPanelSelector("Duration Chart"));
                                },
                            },
                            {
                                element: "Table view",
                                onClickHandler: () => {
                                    dispatch(setLeftPanelSelector("Table view"));
                                },
                            },
                        ]}
                        textSizeDecimal={textSize * 1.2}
                        selected={selectedChart}
                    />
                </a-entity>

                {selectedChart === "Duration Chart" ? (
                    <>
                        <Text
                            text="min duration for each run (minutes)"
                            width={innerWidth}
                            sizeDecimal={textSize * 1.5}
                            position={`${(-innerWidth / 2) * 0.9} ${innerHeight * 0.11
                                } 0.001`}
                        />
                        <BarPanelAux
                            flat
                            stateName="lastTenRunsChart"
                            width={innerWidth}
                            height={innerHeight * 0.325}
                            position={`0 ${-innerHeight * 0.05} 0`}
                        />
                    </>
                ) : (
                    <TableAdvanced
                        width={innerWidth * 0.95}
                        height={innerHeight * 0.35}
                        position={`0 ${innerHeight * 0.125} 0`}
                        aframeClasses={canModify ? "collidable" : ""}
                        textSizeDecimal={textSize * 1.3}
                        data={[
                            ["Scheduled date", "Start date", "Duration"],
                            ...(lastTenRunsTable
                                ? lastTenRunsTable.map((x) => {
                                    return [
                                        getFormattedTime(x[0]),
                                        getFormattedTime(x[1]) || "Not started",
                                        x[2],
                                    ];
                                })
                                : []),
                        ]}
                    />
                )}
            </Panel>

            <JobTooltip
                textSizeDecimal={textSize}
                backgroundColor={"#262626"}
                width={innerWidth - padding}
                height={innerHeight * 0.5 - padding}
                position={`0 ${innerHeight / 2 - (innerHeight * 0.5 - padding) / 2
                    } 0.001`}
                element={{
                    ...selectedJob,
                    lastDefinitionChanges:
                        lastDefinitionChangesData && lastDefinitionChangesData[0]
                            ? lastDefinitionChangesData[0][0]
                            : null,
                }}
            />
        </BackgroundPanel>
    );
});

export default LeftPanelDesktop